<template>
  <div class="card shadow rounded-custom w-100 h-100 overflow-auto d-flex justify-content-center align-items-center">

    <template v-if="showActions === false">
      <div class="show-action d-flex w-100 h-100 flex-column justify-content-evenly align-items-center" @click="expandToggle()">
        <img src="@/assets/icons/manager/gears.svg" width="70" height="70" alt="gears">
        <p class="text-decoration-underline text-violet">Mostra azioni</p>
      </div>
    </template>
    <template v-else>
      <div class="actions-grid">
        <div
          v-for="action in actions"
          :key="action.name"
          class="action-item"
          :class="{ disabled: action.disabled }"
          @click="onAction(action)"
        >
          <img v-if="!(action.name === 'changeDate' && action.disabled)" :src="require(`@/assets/icons/${action.icon}`)" width="50" alt="action.name" />
          <span :class="[action.colorClass, 'text-center']">{{ $t(action.label) }}</span>
          <Spinner v-if="action.action === 'copyName' && isCopying" />
        </div>
        <!-- toggle -->
        <div class="action-item" @click="expandToggle()">
          <img src="@/assets/icons/manager/chevron-rigth-violet.svg" width="50" alt="chevron-rigth-violet" />
          <span class="text-decoration-underline text-violet">Riduci</span>
        </div>
      </div>
      <div v-if="isPrescriptionScanNameCopied" class="d-flex justify-content-center mt-2">
        <p>{{ fileName }}</p>
        <img class="mb-4 ms-1 cursor-pointer" src="@/assets/icons/copy.svg" width="20" alt="resume" @click="copyPrescriptionScanName">
      </div>

    </template>

  </div>
</template>

<script>
import { computed, inject, ref, watch, onMounted } from 'vue';
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { error } from "@/use/toast/error";
import { success } from "@/use/toast/success";
import { isTechnicianIso } from "@/use/utilities/isTechnicianIso";
import { isCurrentDepartment } from "@/use/utilities/isCurrentDepartment";
import isRemotePrintableClinic from "@/use/utilities/isRemotePrintableClinic";
import { getPrescriptionScanName, copyPrescriptionScanName } from '@/utils/prescriptionScanName';
import { update as updateTestWorkQueue } from "@/use/repositories/testWorkQueue/update";
import Spinner from "@/components/general/Spinner";
import { useManagerActions } from '@/composables/useManagerActions';
import { useTypeChecker } from '@/composables/useTypeChecker';
import { checkPendingRequest } from '@/use/repositories/clinic-deliveries/checkPendingRequest';
export default {
  name: "ManagerActions",
  components: {
    Spinner
  },
  props: {
    isDigitalCheckIn: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      required: true
    },
    hasChildren: {
      type: Number,
      required: false
    },
    hasParent: {
      type: Number,
      required: false
    },
    isMobileDepartment: {
      type: Boolean,
      required: false
    },
    canChangeTest: {
      type: Boolean,
      required: true,
      default: true,
    },
    testWorkQueueId: {
      type: Number,
      required: true
    },
    testWorkQueuePhaseId: {
      type: Number,
      required: true
    },
    testWorkQueuePhaseData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    prescription: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: [
    'open',
    'action',
    'external',
    'remotePrint',
  ],
  setup(props, {emit}) {
    const { states, departments, testWorkQueueTypes } = inject("constants");
    const i18n = useI18n();
    const route = useRoute();
    const showActions = ref(false);
    const isPrescriptionScanNameCopied = ref(false);
    const isCopying = ref(false);
    const fileName = ref(null);
    const isIsoDepartment = ref(! props.isDigitalCheckIn && isTechnicianIso());
    const testWorkQueueTypesList = ref([]);
    const initialTestWorkQueueTypesList = ref([]);
    const { extractSlug, hasUnchangedType } = useTypeChecker();
    const isFailedWork = computed(() => {
      return  route.query.test_work_queue_phase_state_slug === states.ISO_FAILED
              ||
              route.query.test_work_queue_phase_state_slug === states.MANAGER_FAILED
    });

    const canPrintOnRemote = computed(() => (
      isCurrentDepartment(departments.MOBILE) ||
      isCurrentDepartment(departments.PROGETTAZIONE) ||
      isCurrentDepartment(departments.FINITURA)
    ));

    const hasPendingDateChangeRequest = ref(false);
    const pendingRequestInfo = ref(null);

    onMounted(async () => {
      if (props.testWorkQueueId) {
        try {
          const pendingCheck = await checkPendingRequest(props.testWorkQueueId);
          hasPendingDateChangeRequest.value = pendingCheck.has_pending_request || false;
          if (pendingCheck.has_pending_request) {
            pendingRequestInfo.value = pendingCheck;
          }
        } catch (err) {
          console.error('Errore nel controllo richiesta pending:', err);
        }
      }
    });

    const { getActionsByMode } = useManagerActions(
      props,
      isPrescriptionScanNameCopied,
      isCopying,
      isRemotePrintableClinic,
      testWorkQueueTypesList,
      isFailedWork,
      canPrintOnRemote,
      initialTestWorkQueueTypesList,
      hasPendingDateChangeRequest,
      pendingRequestInfo
    );

    const mode = computed(() => {
      if (props.isDigitalCheckIn) return 'digitalCheckIn';
      if (route.query.by_manager) return 'manager';
      if (isIsoDepartment.value) return 'isoDepartment';
      return 'default';
    });

    const actions = getActionsByMode(mode.value);

    const isActiveType = (type) => {
      if (typeof type !== 'object') return true;
      return !type.deleted_at && !type.pivot?.deleted_at;
    };

    const readTypesFromProps = () => {
      const types = props.testWorkQueuePhaseData?.test_work_queue?.types || [];
      // Keep as objects to preserve future types; extractSlug handles objects.
      const allTypes = Array.isArray(types) ? [...types] : [];
      initialTestWorkQueueTypesList.value = allTypes;
      testWorkQueueTypesList.value = allTypes.filter(isActiveType);
    };

    readTypesFromProps();

    const copyPrescriptionScanNameHandler = async () => {
      fileName.value = getPrescriptionScanName(props.prescription, props.testWorkQueueId);
      isCopying.value = true;
      await copyPrescriptionScanName(fileName.value, {
        i18n: i18n.t,
        success,
        error,
      });
      setTimeout(() => {
        isCopying.value = false;
        isPrescriptionScanNameCopied.value = true;
      }, 1000);
    };

    const expandToggle = () => {
      // Toggle template.
      showActions.value = ! showActions.value;
      emit('open');
    }

    const setTestWorkQueueType = async (payload) => {
      try {
        await updateTestWorkQueue({
          id: props.testWorkQueuePhaseData?.test_work_queue?.id,
          test_work_queue_phase_id: props.testWorkQueuePhaseData?.id,
          type_slug: payload,
          action: "attach_type",
        });

        if (payload === testWorkQueueTypes.ISO_SKIPPED)
          setTimeout(() => success(i18n.t("ISO marked as ignored")), 2000);

      } catch (error) {
        console.error(error);
      }
    };

    const detachTestWorkQueueType = async (payload) => {
      try {
        await updateTestWorkQueue({
          id: props.testWorkQueuePhaseData.test_work_queue.id,
          test_work_queue_phase_id: props.testWorkQueuePhaseData?.id,
          type_slug: payload,
          action: "detach_type",
        });
      } catch (error) {
        console.error(error);
      }
    };

    const applyIsoTypesDiff = async () => {
      const extract = (item) => extractSlug(item);
      const initialSlugs = new Set(
        initialTestWorkQueueTypesList.value.filter(isActiveType).map(extract)
      );
      const currentSlugs = new Set(testWorkQueueTypesList.value.map(extract));

      for (const slug of currentSlugs) {
        if (!initialSlugs.has(slug) && slug !== testWorkQueueTypes.UNCHANGED) {
          await setTestWorkQueueType(slug);
        }
      }

      for (const slug of initialSlugs) {
        if (!currentSlugs.has(slug) && slug !== testWorkQueueTypes.UNCHANGED) {
          await detachTestWorkQueueType(slug);
        }
      }
    };

    const setAction = async (action, payload) => {
      if (action === states.COMPLETED) {
        await applyIsoTypesDiff();
        if (!isIsoDepartment.value && payload) {
          await setTestWorkQueueType(payload);
        }
      }

      const useByUnchanged = hasUnchangedType(testWorkQueueTypesList.value);

      return emit("action", action, { useByUnchanged });
    };

    const onAction = (param) => {
      const { action, payload } = param;

      if (typeof action === 'function') return action();
      if (action === 'handleType') return handleType(payload);
      if (action === 'copyName') return copyPrescriptionScanNameHandler();
      if (action === 'external') return emit('external');
      if (action === 'remotePrint') return emit('remotePrint');

      return setAction(action, payload);
    };

    const handleType = async (typeSlug) => {
      if (typeSlug == null) return;

      const typeExists = testWorkQueueTypesList.value.some(item => extractSlug(item) === typeSlug);

      if (typeSlug === testWorkQueueTypes.UNCHANGED) {
        if (typeExists) {
          await detachTestWorkQueueType(typeSlug);
          testWorkQueueTypesList.value = testWorkQueueTypesList.value.filter(item => extractSlug(item) !== typeSlug);
        } else {
          await setTestWorkQueueType(typeSlug);
          testWorkQueueTypesList.value = [...testWorkQueueTypesList.value, typeSlug];
        }
        return;
      }

      if (typeExists) {
        testWorkQueueTypesList.value = testWorkQueueTypesList.value.filter(item => extractSlug(item) !== typeSlug);
      } else {
        testWorkQueueTypesList.value = [...testWorkQueueTypesList.value, typeSlug];
      }
    };

    watch(() => props.testWorkQueuePhaseData, () => {
      readTypesFromProps();
    }, { deep: true });

    watch(() => props.disabled, (value) => {
      if (value === false) {
          expandToggle();
      }
    });

    return {
      expandToggle,
      showActions,
      setAction,
      copyPrescriptionScanName: copyPrescriptionScanNameHandler,
      isCopying,
      isPrescriptionScanNameCopied,
      states,
      isFailedWork,
      emit,
      route,
      isIsoDepartment,
      fileName,
      canPrintOnRemote,
      mode,
      onAction,
      actions,
    }
  }
}
</script>

<style lang="scss" scoped>
  .show-action {
    &:hover {
      cursor: pointer;
    }
  }
  .disabled {
    opacity: 0.2;
    cursor: not-allowed !important;
    pointer-events: none;
  }
  .icon {
    &:hover {
      cursor: pointer;
    }
  }

  .actions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
    gap: 1rem;
    padding: 0.5rem;
    width: 100%;
  }

  .action-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
  }
</style>
